import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageTransition from "../components/PageTransition";
import DateRangePicker from "../components/DateRangePicker";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import {
    FaSearch,
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaInfoCircle,
    FaCopy,
    FaTimesCircle,
    FaCheckCircle,
    FaClock,
    FaUniversity,
} from "react-icons/fa";

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 5) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(num);
}

const EMPTY_META_BUCKET = { count: 0, amount: 0, usdt: 0 };
const EMPTY_META = {
    pending: { ...EMPTY_META_BUCKET },
    success: { ...EMPTY_META_BUCKET },
    rejected: { ...EMPTY_META_BUCKET },
    total: { ...EMPTY_META_BUCKET },
};

function parseMetaBucket(raw) {
    return {
        count: Number(raw?.count || 0),
        amount: Number(raw?.amount || 0),
        usdt: Number(raw?.usdt || 0),
    };
}

function normalizeMeta(raw) {
    if (!raw || typeof raw !== "object") return { ...EMPTY_META };
    return {
        pending: parseMetaBucket(raw.pending),
        success: parseMetaBucket(raw.success),
        rejected: parseMetaBucket(raw.rejected),
        total: parseMetaBucket(raw.total),
    };
}

/** Both empty OK; one filled invalid; both filled must be valid YYYY-MM-DD range. */
function validateOptionalDateRange(from, to) {
    const f = String(from || "").trim();
    const t = String(to || "").trim();
    if (!f && !t) return null;
    if (!f || !t) return "from_date and to_date both are required";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f) || !/^\d{4}-\d{2}-\d{2}$/.test(t)) {
        return "Dates must be in YYYY-MM-DD format";
    }
    if (f > t) return "From Date cannot be greater than To Date";
    return null;
}

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return safeText(iso);
    return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

async function copyToClipboard(text) {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_) { }

    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.left = "-1000px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
    } catch (_) {
        return false;
    }
}

function CopyBtn({ value, label = "Copied", disabled }) {
    const isDisabled = disabled || value === null || value === undefined || value === "" || value === "—";
    const onCopy = async () => {
        const ok = await copyToClipboard(String(value));
        if (ok) toast.success(label);
        else toast.error("Copy failed");
    };
    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-secondary border-0 p-1"
            onClick={onCopy}
            disabled={isDisabled}
            title="Copy"
        >
            <FaCopy size={12} />
        </button>
    );
}

function StatusBadge({ status }) {
    const s = String(status);
    if (s === "0") return <span className="fw-semibold text-warning">Pending</span>;
    if (s === "1") return <span className="fw-semibold text-success">Success</span>;
    if (s === "2") return <span className="fw-semibold text-danger">Rejected</span>;
    return <span className="fw-semibold text-muted">Unknown</span>;
}

function MetaSummaryCard({ title, bucket, tone }) {
    const tones = {
        pending: { border: "border-warning", titleClass: "text-warning", bg: "bg-warning-subtle" },
        success: { border: "border-success", titleClass: "text-success", bg: "bg-success-subtle" },
        rejected: { border: "border-danger", titleClass: "text-danger", bg: "bg-danger-subtle" },
        total: { border: "border-primary", titleClass: "text-primary", bg: "bg-primary-subtle" },
    };
    const t = tones[tone] || tones.total;
    return (
        <div className={`card h-100 border ${t.border}`}>
            <div className={`card-body py-3 ${t.bg}`}>
                <div className={`small fw-semibold text-uppercase mb-1 ${t.titleClass}`}>{title}</div>
                <div className="fw-bold fs-5 mb-1">{nfmt(bucket.count, 0)}</div>
                <div className="small text-muted">₹ {nfmt(bucket.amount)}</div>
                <div className="small text-muted">{nfmt(bucket.usdt)} USDT</div>
            </div>
        </div>
    );
}

function getBankSummary(ex) {
    const bank = ex?.bank && typeof ex.bank === "object" ? ex.bank : null;
    if (!bank) {
        return {
            type: "",
            title: "Account",
            primary: safeText(ex?.upi_id || ex?.bank_id),
            secondary: "",
            holder: "",
            ifsc: "",
            accountNo: "",
            bankName: "",
            upiId: safeText(ex?.upi_id),
        };
    }

    const type = String(bank.type || "").toLowerCase() === "upi" ? "upi" : "bank";
    const upiId = String(bank.upi_id || (type === "upi" ? bank.account_no : "") || "").trim();
    const accountNo = String(bank.account_no || "").trim();
    const holder = String(bank.name || bank.account_holder || "").trim();
    const bankName = String(bank.bank_name || "").trim();
    const ifsc = String(bank.ifsc_code || "").trim();

    if (type === "upi") {
        return {
            type: "upi",
            title: "UPI",
            primary: upiId || accountNo || "—",
            secondary: holder,
            holder,
            ifsc: "",
            accountNo,
            bankName: "",
            upiId: upiId || accountNo,
        };
    }

    return {
        type: "bank",
        title: bankName || "Bank",
        primary: accountNo || "—",
        secondary: [holder, ifsc].filter(Boolean).join(" · "),
        holder,
        ifsc,
        accountNo,
        bankName: bankName || "—",
        upiId: "",
    };
}

function LoadingRow({ cols = 8 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <div className="placeholder-glow">
                        <span className="placeholder col-12" />
                    </div>
                </td>
            ))}
        </tr>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Number(totalPages || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex justify-content-between align-items-center">
            <div>
                <span className="text-muted small">
                    Page {pageNo} of {totalPages}
                </span>
            </div>

            <div className="d-flex align-items-center gap-1">
                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft />
                </button>

                <select
                    className="form-select form-select-sm mx-1"
                    style={{ width: "auto" }}
                    value={pageNo}
                    onChange={(e) => onJump(Number(e.target.value))}
                    disabled={loading || totalPages <= 1}
                >
                    {pages.map((p) => (
                        <option key={p} value={p}>
                            {p}
                        </option>
                    ))}
                </select>

                <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    <FaChevronRight />
                </button>
            </div>
        </div>
    );
}

function StatusConfirmModal({ show, type, exchangeId, onClose, onConfirm, loading }) {
    const [rejectReason, setRejectReason] = useState("");

    useEffect(() => {
        if (show) {
            setRejectReason("");
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && show) onClose();
        };
        if (show) document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [show, onClose]);

    if (!show) return null;

    const title = type === "success" ? "Confirm Approve Withdrawal" : "Confirm Reject Withdrawal";
    const confirmText = type === "success" ? "Approve" : "Reject";
    const confirmColor = type === "success" ? "btn-success" : "btn-danger";
    const icon = type === "success" ? <FaCheckCircle className="me-2" /> : <FaTimesCircle className="me-2" />;

    const handleConfirm = () => {
        if (type === "reject" && !rejectReason.trim()) {
            toast.error("Reject reason is required");
            return;
        }
        onConfirm({
            exchange_id: exchangeId,
            status: type === "success" ? "1" : "2",
            reject_reason: type === "reject" ? rejectReason.trim() : "",
        });
    };

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1040,
                }}
                onClick={onClose}
            />

            <div
                className="modal fade show"
                style={{
                    display: "block",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1050,
                    overflowX: "hidden",
                    overflowY: "auto",
                }}
                tabIndex="-1"
                role="dialog"
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    style={{ maxWidth: "520px", margin: "1.75rem auto" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title mb-0">{title}</h5>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                        </div>

                        <div className="modal-body">
                            {type === "reject" ? (
                                <>
                                    <div className="text-muted mb-2">
                                        Rejecting will refund USDT to the user via reverse P2P.
                                    </div>
                                    <div className="mb-2 text-danger fw-semibold">Reject reason is required</div>
                                    <label className="form-label fw-semibold">
                                        Reject Reason <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder="Enter reject reason..."
                                        required
                                        autoFocus
                                    />
                                </>
                            ) : (
                                <div className="text-muted">
                                    Approve this sell withdrawal? This only marks the exchange as success (no extra
                                    USDT transfer).
                                </div>
                            )}
                        </div>

                        <div className="modal-footer bg-light">
                            <button
                                type="button"
                                className={`btn ${confirmColor}`}
                                onClick={handleConfirm}
                                disabled={loading || (type === "reject" && !rejectReason.trim())}
                            >
                                {icon}
                                {loading ? "Processing..." : confirmText}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function DetailsModal({ show, item, onClose, onApprove, onReject, actionLoading }) {
    useEffect(() => {
        if (show) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [show]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && show) onClose();
        };
        if (show) document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [show, onClose]);

    if (!show || !item) return null;

    const ex = item?.exchange || {};
    const u = item?.user || {};
    const bank = getBankSummary(ex);
    const canAct = String(ex?.status) === "0";

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 1040,
                }}
                onClick={onClose}
            />

            <div
                className="modal fade show"
                style={{
                    display: "block",
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1050,
                    overflowX: "hidden",
                    overflowY: "auto",
                }}
                tabIndex="-1"
                role="dialog"
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-centered"
                    style={{ maxWidth: "950px", margin: "1.75rem auto" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-light">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="modal-title mb-0">Exchange Withdrawal Details</h5>
                                <StatusBadge status={ex?.status} />
                            </div>
                            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                        </div>

                        <div className="modal-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="card h-100 border">
                                        <div className="card-header bg-white border-bottom">
                                            <h6 className="mb-0 fw-semibold">Withdrawal</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="row mb-3">
                                                <div className="col-12">
                                                    <div className="text-muted small mb-1">Created</div>
                                                    <div>{formatDate(ex?.create_date)}</div>
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">INR Amount</div>
                                                    <div className="fw-bold fs-5">₹ {nfmt(ex?.amount)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">USDT</div>
                                                    <div className="fw-bold fs-5">{nfmt(ex?.usdt)}</div>
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">Exchange ID</div>
                                                    <div className="fw-semibold d-flex align-items-center gap-1">
                                                        <span className="text-break">{safeText(ex?.exchange_id)}</span>
                                                        <CopyBtn value={ex?.exchange_id} label="Exchange ID copied" />
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">Type</div>
                                                    <div className="fw-semibold text-uppercase">
                                                        {safeText(ex?.type || "sell")}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mb-0">
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">From</div>
                                                    <div className="fw-semibold">{safeText(ex?.exchange_from)}</div>
                                                </div>
                                                <div className="col-6">
                                                    <div className="text-muted small mb-1">To</div>
                                                    <div className="fw-semibold">{safeText(ex?.exchange_to)}</div>
                                                </div>
                                            </div>

                                            {String(ex?.status) === "2" && ex?.reject_reason && (
                                                <div className="mt-3 p-2 border border-danger-subtle rounded bg-danger-subtle">
                                                    <div className="text-danger small fw-semibold mb-1">Reject reason</div>
                                                    <div>{safeText(ex.reject_reason)}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="card h-100 border">
                                        <div className="card-header bg-white border-bottom">
                                            <h6 className="mb-0 fw-semibold">User</h6>
                                        </div>
                                        <div className="card-body">
                                            <div className="fw-semibold mb-1">{safeText(u?.name)}</div>
                                            <div className="text-muted small">
                                                Username: <span className="fw-semibold">{safeText(u?.username)}</span>
                                            </div>
                                            <div className="text-muted small">
                                                User ID: <span className="fw-semibold">{safeText(u?.user_id)}</span>
                                            </div>
                                            <div className="text-muted small">
                                                Mobile: {safeText(u?.mobile)} ({safeText(u?.country_code)})
                                            </div>
                                            <div className="text-muted small">Email: {safeText(u?.email)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="card border">
                                        <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2">
                                                <FaUniversity className="text-muted" />
                                                <h6 className="mb-0 fw-semibold">Payout Account</h6>
                                            </div>
                                            <span className="badge text-bg-light border">
                                                {bank.type === "upi"
                                                    ? "UPI"
                                                    : bank.type === "bank"
                                                        ? "Bank Transfer"
                                                        : "Account"}
                                            </span>
                                        </div>
                                        <div className="card-body">
                                            {bank.type === "upi" ? (
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">UPI ID</div>
                                                        <div className="fw-semibold d-flex align-items-center gap-1">
                                                            <span className="text-break">
                                                                {safeText(bank.upiId || bank.primary)}
                                                            </span>
                                                            <CopyBtn
                                                                value={bank.upiId || bank.primary}
                                                                label="UPI ID copied"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">Name</div>
                                                        <div className="fw-semibold">{safeText(bank.holder)}</div>
                                                    </div>
                                                </div>
                                            ) : bank.type === "bank" ? (
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">Bank name</div>
                                                        <div className="fw-semibold d-flex align-items-center gap-1">
                                                            <span className="text-break">
                                                                {safeText(bank.bankName || bank.title)}
                                                            </span>
                                                            <CopyBtn
                                                                value={bank.bankName || bank.title}
                                                                label="Bank name copied"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">Account holder</div>
                                                        <div className="fw-semibold d-flex align-items-center gap-1">
                                                            <span className="text-break">{safeText(bank.holder)}</span>
                                                            <CopyBtn value={bank.holder} label="Account holder copied" />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">Account number</div>
                                                        <div className="fw-semibold d-flex align-items-center gap-1">
                                                            <span className="text-break">
                                                                {safeText(bank.accountNo || bank.primary)}
                                                            </span>
                                                            <CopyBtn
                                                                value={bank.accountNo || bank.primary}
                                                                label="Account number copied"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">IFSC</div>
                                                        <div className="fw-semibold d-flex align-items-center gap-1">
                                                            <span>{safeText(bank.ifsc)}</span>
                                                            <CopyBtn value={bank.ifsc} label="IFSC copied" />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="row g-3">
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">UPI / Account</div>
                                                        <div className="fw-semibold d-flex align-items-center gap-1">
                                                            <span className="text-break">{bank.primary}</span>
                                                            <CopyBtn value={bank.primary} label="Copied" />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="text-muted small mb-1">Bank ID</div>
                                                        <div className="fw-semibold">{safeText(ex?.bank_id)}</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer bg-light">
                            <button type="button" className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                            {canAct && (
                                <div className="d-flex gap-2 flex-wrap justify-content-end ms-auto">
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={() => onApprove?.(ex?.exchange_id)}
                                        disabled={actionLoading}
                                        title="Approve (success)"
                                    >
                                        <FaCheckCircle className="me-2" />
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => onReject?.(ex?.exchange_id)}
                                        disabled={actionLoading}
                                        title="Reject (rejected)"
                                    >
                                        <FaTimesCircle className="me-2" />
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default function P2PWithdrawalList() {
    const [loading, setLoading] = useState(true);
    const [inputText, setInputText] = useState("");
    const [appliedQuery, setAppliedQuery] = useState("");
    const [tab, setTab] = useState("all");
    const [pageNo, setPageNo] = useState(1);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [list, setList] = useState([]);
    const [info, setInfo] = useState({ total_exchange: 0, page_no: 1 });
    const [meta, setMeta] = useState(EMPTY_META);
    const [selectedItem, setSelectedItem] = useState(null);
    const [statusConfirm, setStatusConfirm] = useState({
        show: false,
        type: null,
        exchange_id: null,
    });
    const [statusLoading, setStatusLoading] = useState(false);
    const lastDateErrorRef = useRef("");

    const PAGE_SIZE = 20;

    const totalPages = useMemo(() => {
        const total = Number(info?.total_exchange || 0);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / PAGE_SIZE));
    }, [info?.total_exchange]);

    const statusParam = useMemo(() => {
        if (tab === "pending") return "0";
        if (tab === "success") return "1";
        if (tab === "rejected") return "2";
        return "";
    }, [tab]);

    const fetchWithdrawals = useCallback(async ({ page, query, status, from_date, to_date }) => {
        const dateErr = validateOptionalDateRange(from_date, to_date);
        if (dateErr) {
            if (lastDateErrorRef.current !== dateErr) {
                lastDateErrorRef.current = dateErr;
                toast.error(dateErr);
            }
            setList([]);
            setInfo({ total_exchange: 0, page_no: page });
            setMeta(EMPTY_META);
            setLoading(false);
            return;
        }
        lastDateErrorRef.current = "";

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/exchange-withdrawal-list`,
                headers: { "Content-Type": "application/json" },
                data: {
                    page_no: page,
                    query: query || "",
                    status: status || "",
                    from_date: from_date || "",
                    to_date: to_date || "",
                    token: localStorage.getItem("token") || "",
                },
            });

            const data = res?.data;
            if (data?.error) {
                toast.error(
                    typeof data?.error === "string" ? data.error : "Failed to load exchange withdrawals"
                );
                setList([]);
                setInfo({ total_exchange: 0, page_no: page });
                setMeta(EMPTY_META);
                return;
            }

            const raw = Array.isArray(data?.exchange_list) ? data.exchange_list : [];
            setList(raw);

            const apiInfo = data?.info || {};
            setInfo({
                total_exchange: Number(apiInfo?.total_exchange || 0),
                page_no: Number(apiInfo?.page_no || page),
            });
            setMeta(normalizeMeta(data?.meta));
        } catch (err) {
            console.log(err);
            toast.error("Failed to load exchange withdrawals");
            setList([]);
            setInfo({ total_exchange: 0, page_no: page });
            setMeta(EMPTY_META);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateWithdrawalStatus = useCallback(
        async ({ exchange_id, status, reject_reason }) => {
            if (!exchange_id) {
                toast.error("Missing exchange information");
                return;
            }

            setStatusLoading(true);
            try {
                const res = await axios({
                    method: "post",
                    url: `${BASE_ENDPOINT}/admin/p2p-withdrawal-status`,
                    headers: { "Content-Type": "application/json" },
                    data: {
                        token: localStorage.getItem("token") || "",
                        exchange_id,
                        status,
                        reject_reason: reject_reason || "",
                    },
                });

                const out = res?.data;
                if (out?.error) {
                    toast.error(
                        out?.error === "session expired"
                            ? "Session expired. Please login again."
                            : out?.error || "Failed to update"
                    );
                    return;
                }

                toast.success(out?.msg || "P2P withdrawal status updated successfully");
                setStatusConfirm({ show: false, type: null, exchange_id: null });
                setSelectedItem(null);
                await fetchWithdrawals({
                    page: pageNo,
                    query: appliedQuery,
                    status: statusParam,
                    from_date: fromDate,
                    to_date: toDate,
                });
            } catch (err) {
                console.error(err);
                toast.error("Failed to update P2P withdrawal status");
            } finally {
                setStatusLoading(false);
            }
        },
        [appliedQuery, fetchWithdrawals, fromDate, pageNo, statusParam, toDate]
    );

    const requestApprove = (exchange_id) => {
        setStatusConfirm({ show: true, type: "success", exchange_id });
    };

    const requestReject = (exchange_id) => {
        setStatusConfirm({ show: true, type: "reject", exchange_id });
    };

    useEffect(() => {
        fetchWithdrawals({
            page: pageNo,
            query: appliedQuery,
            status: statusParam,
            from_date: fromDate,
            to_date: toDate,
        });
    }, [pageNo, appliedQuery, statusParam, fromDate, toDate, fetchWithdrawals]);

    const onSearch = () => {
        setPageNo(1);
        setAppliedQuery(inputText.trim());
    };

    const onDateRangeChange = ({ from_date, to_date }) => {
        setFromDate(from_date || "");
        setToDate(to_date || "");
        setPageNo(1);
    };

    const onRefreshReset = async () => {
        setInputText("");
        setAppliedQuery("");
        setTab("all");
        setFromDate("");
        setToDate("");
        setPageNo(1);
        await fetchWithdrawals({
            page: 1,
            query: "",
            status: "",
            from_date: "",
            to_date: "",
        });
    };

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));

    const openDetails = (item) => setSelectedItem(item);
    const closeDetails = () => setSelectedItem(null);

    return (
        <PageTransition>
            <div className="container-fluid py-3">
                <div className="row mb-3">
                    <div className="col">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h1 className="h3 mb-1">Exchange Withdrawals</h1>
                                <p className="text-muted mb-0">
                                    Sell withdrawals (USDT → INR) • Total:{" "}
                                    <strong>{nfmt(info.total_exchange, 0)}</strong>
                                    {appliedQuery && (
                                        <span className="ms-2">
                                            • Search: <strong>{appliedQuery}</strong>
                                        </span>
                                    )}
                                    {(fromDate || toDate) && (
                                        <span className="ms-2">
                                            • Date:{" "}
                                            <strong>
                                                {fromDate || "—"} → {toDate || "—"}
                                            </strong>
                                        </span>
                                    )}
                                </p>
                            </div>
                            <button className="btn btn-outline-primary" onClick={onRefreshReset} disabled={loading}>
                                <FaSyncAlt className={`me-2 ${loading ? "spin" : ""}`} />
                                {loading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Pending" bucket={meta.pending} tone="pending" />
                    </div>
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Success" bucket={meta.success} tone="success" />
                    </div>
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Rejected" bucket={meta.rejected} tone="rejected" />
                    </div>
                    <div className="col-6 col-md-3">
                        <MetaSummaryCard title="Total" bucket={meta.total} tone="total" />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                    <ul className="nav nav-tabs border-0">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "all" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("all");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaSearch className="me-2" />
                                                All
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "pending" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("pending");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaClock className="me-2" />
                                                Pending
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "success" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("success");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaCheckCircle className="me-2" />
                                                Success
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${tab === "rejected" ? "active" : ""}`}
                                                onClick={() => {
                                                    setTab("rejected");
                                                    setPageNo(1);
                                                }}
                                            >
                                                <FaTimesCircle className="me-2" />
                                                Rejected
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="d-flex align-items-center gap-2">
                                        <div className="input-group input-group-sm" style={{ width: "320px" }}>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search name/mobile/email/user id/exchange id..."
                                                value={inputText}
                                                onChange={(e) => setInputText(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && onSearch()}
                                            />
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={onSearch}
                                                disabled={loading}
                                            >
                                                <FaSearch />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-2 align-items-end">
                                    <div className="col-12 col-md-5 col-lg-4">
                                        <DateRangePicker
                                            fromDate={fromDate}
                                            toDate={toDate}
                                            onChange={onDateRangeChange}
                                            disabled={loading}
                                            label="Date range"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <div className="card">
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "5%" }}>#</th>
                                                <th style={{ width: "14%" }}>Date</th>
                                                <th style={{ width: "15%" }}>User</th>
                                                <th style={{ width: "20%" }}>Bank / UPI</th>
                                                <th style={{ width: "12%" }}>INR</th>
                                                <th style={{ width: "10%" }}>USDT</th>
                                                <th style={{ width: "10%" }}>Status</th>
                                                <th style={{ width: "10%" }}>Actions</th>
                                                <th style={{ width: "6%" }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[...Array(5)].map((_, i) => (
                                                        <LoadingRow key={i} cols={9} />
                                                    ))}
                                                </>
                                            ) : list.length === 0 ? (
                                                <tr>
                                                    <td colSpan={9} className="text-center py-5">
                                                        <div className="text-muted">
                                                            <FaSearch className="mb-2" size={24} />
                                                            <p className="mb-0">No withdrawals found</p>
                                                            {appliedQuery && (
                                                                <small>Try changing your search query</small>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                list.map((row, index) => {
                                                    const ex = row?.exchange || {};
                                                    const u = row?.user || {};
                                                    const bank = getBankSummary(ex);
                                                    const canAct = String(ex?.status) === "0";
                                                    const serialNo = (pageNo - 1) * PAGE_SIZE + index + 1;

                                                    return (
                                                        <tr key={ex?.exchange_id}>
                                                            <td className="text-muted">{serialNo}</td>
                                                            <td>
                                                                <div className="fw-medium">
                                                                    {formatDate(ex?.create_date)}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">{safeText(u?.name)}</div>
                                                                <div className="text-muted small">
                                                                    ID: {safeText(u?.user_id)}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    {bank.type === "upi"
                                                                        ? `UPI · ${bank.primary}`
                                                                        : `${bank.title} · ${bank.primary}`}
                                                                </div>
                                                                <div className="text-muted small">
                                                                    {bank.secondary || safeText(bank.holder)}
                                                                </div>
                                                            </td>
                                                            <td className="fw-bold">₹ {nfmt(ex?.amount)}</td>
                                                            <td>{nfmt(ex?.usdt)}</td>
                                                            <td>
                                                                <StatusBadge status={ex?.status} />
                                                            </td>
                                                            <td>
                                                                {canAct ? (
                                                                    <div className="d-flex gap-1 flex-wrap">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-success"
                                                                            onClick={() =>
                                                                                requestApprove(ex?.exchange_id)
                                                                            }
                                                                            disabled={statusLoading}
                                                                            title="Approve"
                                                                        >
                                                                            <FaCheckCircle />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger"
                                                                            onClick={() =>
                                                                                requestReject(ex?.exchange_id)
                                                                            }
                                                                            disabled={statusLoading}
                                                                            title="Reject"
                                                                        >
                                                                            <FaTimesCircle />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted small">—</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    onClick={() => openDetails(row)}
                                                                    title="View Details"
                                                                >
                                                                    <FaInfoCircle />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {!loading && list.length > 0 && (
                                    <div className="card-footer">
                                        <PaginationBar
                                            loading={loading}
                                            pageNo={pageNo}
                                            totalPages={totalPages}
                                            onPrev={onPrev}
                                            onNext={onNext}
                                            onJump={onJump}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <DetailsModal
                    show={!!selectedItem}
                    item={selectedItem}
                    onClose={closeDetails}
                    onApprove={requestApprove}
                    onReject={requestReject}
                    actionLoading={statusLoading}
                />

                <StatusConfirmModal
                    show={statusConfirm.show}
                    type={statusConfirm.type}
                    exchangeId={statusConfirm.exchange_id}
                    onClose={() => setStatusConfirm({ show: false, type: null, exchange_id: null })}
                    loading={statusLoading}
                    onConfirm={updateWithdrawalStatus}
                />

                <style jsx>{`
                    .nav-tabs .nav-link {
                        border: none;
                        color: #6c757d;
                        padding: 0.5rem 1rem;
                    }
                    .nav-tabs .nav-link.active {
                        color: #0d6efd;
                        border-bottom: 2px solid #0d6efd;
                        background: transparent;
                    }
                    .nav-tabs .nav-link:hover:not(.active) {
                        color: #495057;
                    }
                    .spin {
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                    .table th {
                        font-weight: 600;
                        font-size: 0.875rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .table td {
                        vertical-align: middle;
                        font-size: 0.9rem;
                    }
                `}</style>
            </div>
        </PageTransition>
    );
}
